/**
 * Printing from the tablet. Tickets and labels come from the API as self-contained HTML sized for
 * the thermal printer (`@page` set by the renderer); PDFs come as bytes. Both are loaded into a
 * hidden same-origin iframe and printed from there, so the staff screen never leaves the flow and
 * the browser's print dialog picks the printer (ticket, label or letter) the user chose last time.
 */

function mountFrame(): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(frame);
  return frame;
}

/**
 * Prints the frame and settles when its print dialog closes (`afterprint`), so a caller can chain
 * a second print without two dialogs colliding. Never rejects: `false` means the browser had no
 * window to print or ignored `print()` (no `beforeprint`), and the caller falls back to the buttons.
 */
function printFrame(frame: HTMLIFrameElement): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const win = frame.contentWindow;
    if (!win) {
      frame.remove();
      resolve(false);
      return;
    }
    let opened = false;
    win.addEventListener("beforeprint", () => (opened = true), { once: true });
    win.addEventListener(
      "afterprint",
      () => {
        // Settle from a fresh task of the main window, never from inside the frame's
        // `afterprint` dispatch: Chrome and Edge drop the microtasks queued while that event is
        // being handled, so a React state update made by the caller's continuation (the chained
        // labels print calls `setBusy`) queued React's flush microtask into the void and the
        // whole page stopped re-rendering — «Ver solicitud» and «Siguiente paciente» did nothing
        // (pilot, 2026-11-02).
        setTimeout(() => resolve(true), 0);
        setTimeout(() => frame.remove(), 60_000);
      },
      { once: true },
    );
    try {
      win.focus();
      win.print();
      // A browser that ignores the call (Chrome throttles script-initiated prints) fires no print
      // events at all: give up after a beat instead of waiting forever for `afterprint`.
      setTimeout(() => {
        if (!opened) resolve(false);
      }, 1500);
    } catch {
      frame.remove();
      resolve(false);
    }
  });
}

/** Prints self-contained HTML; resolves `true` when the print dialog closes, `false` if it could not open. */
export function printHtml(html: string): Promise<boolean> {
  const frame = mountFrame();
  const done = new Promise<boolean>((resolve) => {
    frame.addEventListener("load", () => void printFrame(frame).then(resolve), { once: true });
  });
  frame.srcdoc = html;
  return done;
}

export function printPdf(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const frame = mountFrame();
  frame.src = url;
  frame.addEventListener(
    "load",
    () => {
      void printFrame(frame);
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    },
    { once: true },
  );
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** wa.me deep link for a 10-digit Mexican number. */
export function whatsappLink(phone10: string, text: string): string {
  const digits = phone10.replace(/\D/g, "").slice(-10);
  return `https://wa.me/52${digits}?text=${encodeURIComponent(text)}`;
}
